import * as Sentry from '@sentry/react';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { T, useTX } from '@transifex/react';
import { AlertCircle, RefreshCcw01 } from '@untitledui-pro/icons/duotone';
import { Button } from 'src/antd';

const SENTRY_DSN = import.meta.env.REACT_APP_SENTRY_DSN;
const SENTRY_ENV = import.meta.env.REACT_APP_ENVIRONMENT;
// REACT_APP_ENVIRONMENT is 'production' on the prod deploy and 'preview' /
// 'development' / <bitbucket-env> elsewhere; it's undefined for local
// `yarn start` (where MODE === 'development').
const isProd = SENTRY_ENV === 'production';
const isLocalDev = import.meta.env.MODE === 'development';

const SENSITIVE_QUERY_PARAMS = ['api_key', 'property_key'];

// Replaces sensitive query-param values in a URL (absolute or relative) with
// [redacted]. Citizens log in via ?api_key=<token> deep links, and the
// /calendar PDF routes keep the token in the URL for the whole session — so
// every URL Sentry attaches (events, breadcrumbs, traces) must be scrubbed.
// Returns the input unchanged when it isn't parseable or carries no
// sensitive params.
export const scrubSensitiveUrl = <T extends string | undefined>(url: T): T => {
  if (!url) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    let changed = false;
    SENSITIVE_QUERY_PARAMS.forEach((param) => {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, '[redacted]');
        changed = true;
      }
    });
    return (changed ? parsed.toString() : url) as T;
  } catch {
    return url;
  }
};

// Skip Sentry entirely on local dev or when there's no DSN, so the shared DSN
// isn't hammered (429) from every developer's machine. Only the init() call is
// gated — the ErrorBoundary exports below work whether or not init ran (an
// un-init'd Sentry simply no-ops on capture).
if (SENTRY_DSN && !isLocalDev) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENV,
    release: import.meta.env.REACT_APP_SENTRY_RELEASE,
    // Performance tracing is what triggered the 429: enable browser tracing in
    // production only, at 10% sampling. Error events are unaffected (governed by
    // the default sampleRate of 1.0).
    integrations: isProd ? [Sentry.browserTracingIntegration()] : [],
    tracesSampleRate: isProd ? 0.1 : 0,
    beforeSend(event) {
      if (event.request?.url)
        event.request.url = scrubSensitiveUrl(event.request.url);
      return event;
    },
    beforeSendTransaction(event) {
      if (event.request?.url)
        event.request.url = scrubSensitiveUrl(event.request.url);
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (typeof breadcrumb.data?.url === 'string')
        breadcrumb.data.url = scrubSensitiveUrl(breadcrumb.data.url);
      if (typeof breadcrumb.data?.from === 'string')
        breadcrumb.data.from = scrubSensitiveUrl(breadcrumb.data.from);
      if (typeof breadcrumb.data?.to === 'string')
        breadcrumb.data.to = scrubSensitiveUrl(breadcrumb.data.to);
      return breadcrumb;
    },
  });
}

export const TestErrorBoundary = () => {
  /* Returns a button which throws an error in the component
   * To Test if error boundary is working fine
   */
  const [error, setError] = useState(false);

  useEffect(() => {
    if (error) {
      throw new Error();
    }
  }, [error]);

  return (
    <Button
      type="primary"
      style={{ zIndex: 1000, position: 'fixed', top: 0, left: 0 }}
      onClick={() => {
        setError(true);
      }}
    >
      <T _str="Throw Error" />
    </Button>
  );
};

const getFallback: (locale: string) => Sentry.FallbackRender =
  (locale) =>
  ({ resetError, eventId }) => {
    return (
      <div className="wh-ui flex h-full w-full items-center justify-center p-6">
        <div className="flex w-full max-w-[400px] flex-col items-center gap-8">
          <AlertCircle
            aria-hidden
            className="size-6 text-[var(--icon-default)]"
          />
          <p className="min-w-full text-center text-[20px] font-normal leading-[1.5] text-[var(--text-secondary)]">
            <T _str="Something went wrong" />
          </p>
          <div className="flex h-12 w-full items-start justify-center gap-4">
            <button
              type="button"
              onClick={() => {
                resetError();
                window.location.reload();
              }}
              className="flex h-12 items-center justify-center gap-4 rounded-[8px] border border-[var(--border-default)] bg-[var(--bg-default)] px-4 text-[16px] font-medium leading-[1.5] text-[var(--text-secondary)] shadow-[0_1px_0_var(--border-default)] transition-colors hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--border-focus)]"
            >
              <RefreshCcw01 className="size-6" aria-hidden />
              <T _str="Try again" />
            </button>
            <button
              type="button"
              onClick={() => {
                Sentry.showReportDialog({
                  lang: locale,
                  dsn: import.meta.env.REACT_APP_SENTRY_DSN,
                  eventId: eventId || undefined,
                });
              }}
              className="flex h-12 items-center justify-center rounded-[8px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-2 text-[16px] font-medium leading-[1.5] text-[var(--text-secondary)] shadow-[0_1px_0_var(--border-default)] transition-colors hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--border-focus)]"
            >
              <T _str="Report" />
            </button>
          </div>
        </div>
      </div>
    );
  };

export const ErrorBoundary: FC<{ children: ReactNode }> = ({ children }) => {
  const tx = useTX();
  const currentLocale = tx.getCurrentLocale();
  const fallback = getFallback(currentLocale);

  return (
    <Sentry.ErrorBoundary fallback={fallback}>{children}</Sentry.ErrorBoundary>
  );
};
